import {
  BlockCategory,
  BlockType,
  editorBlocksLibrary,
} from "@babackapp/editor-blocks-library/blocks";
import { AddBlockIcon } from "./add-block-icon";
import { DraggableBlock } from "./draggable-block";
import { useTranslation } from "react-i18next";
import { ContextualTipsTrigger } from "@babackapp/ui/components/contextual-tips";
import { useTemplatesEditorConfig } from "../context/templates-editor-config-context";

import type { TemplateType } from "../types";

const CATEGORY_LABELS: Record<BlockCategory, string> = {
  [BlockCategory.LAYOUT]:
    "editorBlocksLibrary.blocksList.category.layout.title",
  [BlockCategory.ESSENTIALS]:
    "editorBlocksLibrary.blocksList.category.essential.title",
  [BlockCategory.TRACKING]:
    "editorBlocksLibrary.blocksList.category.tracking.title",
  [BlockCategory.RETURN]:
    "editorBlocksLibrary.blocksList.category.return.title",
};

export function PanelBlocksList({
  templateType = "email",
}: {
  templateType?: TemplateType;
}) {
  const { t } = useTranslation("trackoffice");
  const { hiddenBlockCategories } = useTemplatesEditorConfig();
  const blocksByCategory = Object.entries(editorBlocksLibrary).reduce<
    Record<
      BlockCategory,
      Array<{
        blockType: BlockType;
        block: NonNullable<
          (typeof editorBlocksLibrary)[keyof typeof editorBlocksLibrary]
        >;
      }>
    >
  >(
    (acc, [blockType, block]) => {
      if (block && block.config.category) {
        const category = block.config.category;
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push({ blockType: blockType as BlockType, block });
      }
      return acc;
    },
    {} as Record<
      BlockCategory,
      Array<{
        blockType: BlockType;
        block: NonNullable<
          (typeof editorBlocksLibrary)[keyof typeof editorBlocksLibrary]
        >;
      }>
    >,
  );

  return (
    <ContextualTipsTrigger
      wrapped
      tips={[
        {
          id: "template-blocks",
          title: t("templatesEditor.tips.templateBlocks.title", "Add a block"),
          description: t(
            "templatesEditor.tips.templateBlocks.description",
            "You can add a block to the template simply by dragging it from the list below. Most of the blocks can be customized to fit your needs.",
          ),
        },
      ]}
    >
      <div className="flex flex-col gap-4 px-2 flex-1">
        {Object.values(BlockCategory).map((category: BlockCategory) => {
          if (hiddenBlockCategories?.includes(category)) return null;
          const blocks = (blocksByCategory[category] ?? []).filter((block) => {
            if (templateType === "email") {
              return !!block.block.EmailEditor;
            } else {
              return !!block.block.WebEditor;
            }
          });
          if (!blocks || blocks.length === 0) return null;

          return (
            <div key={category} className="flex flex-col gap-2 flex-1">
              <span className="text-xs font-medium text-muted-foreground">
                {t(CATEGORY_LABELS[category])}
              </span>
              <div className="flex flex-col gap-1 flex-1">
                {blocks.map(
                  ({
                    blockType,
                    block,
                  }: {
                    blockType: BlockType;
                    block: NonNullable<
                      (typeof editorBlocksLibrary)[keyof typeof editorBlocksLibrary]
                    >;
                  }) => (
                    <DraggableBlock
                      key={blockType}
                      blockType={blockType}
                      title={t(block.config.name)}
                      description={t(block.config.description)}
                      icon={block.config.Icon}
                    >
                      <AddBlockIcon
                        title={t(block.config.name)}
                        description={t(block.config.description)}
                        icon={block.config.Icon}
                        hasDynamicContent={block.config.hasDynamicContent}
                      />
                    </DraggableBlock>
                  ),
                )}
              </div>
            </div>
          );
        })}
      </div>
    </ContextualTipsTrigger>
  );
}
